import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, UserPlus, MessageCircle, Coffee, Activity, Flame, Bell, Megaphone, Users } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const TYPES = [
  { k: "friend_request", l: "Friend Requests", d: "Someone wants to connect", icon: UserPlus, color: "#2fbf71" },
  { k: "friend_accepted", l: "Friend Accepted", d: "A request you sent was accepted", icon: UserPlus, color: "#2fbf71" },
  { k: "message", l: "Direct Messages", d: "New 1-on-1 messages", icon: MessageCircle, color: "#00B4D8" },
  { k: "circle_message", l: "Circle Messages", d: "Activity in your Posture Circles™", icon: Users, color: "#00B4D8" },
  { k: "coach_insight", l: "AI Coach Insights", d: "Suggestions from your coach", icon: Activity, color: "#a78bfa" },
  { k: "streak", l: "Streak Reminders", d: "Don't break your momentum", icon: Flame, color: "#F59E0B" },
  { k: "system", l: "System & Announcements", d: "Important platform updates", icon: Megaphone, color: "#94A3B8" },
];

export default function SettingsNotifications() {
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notifications/prefs").then((r) => setPrefs(r.data));
  }, []);

  const toggle = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    setSaving(true);
    try {
      await api.put("/notifications/prefs", next);
      toast.success("Saved");
    } catch (e) {
      toast.error("Failed to save");
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <PhoneFrame><div className="p-6 text-slate-400">Loading…</div></PhoneFrame>;

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="prefs-back">
          <ChevronLeft size={16} /> Back
        </button>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center">
            <Bell size={20} color="#F59E0B" />
          </div>
          <div>
            <h1 className="ps-heading text-2xl font-bold">Notifications</h1>
            <p className="text-slate-400 text-sm">Choose what reaches you.</p>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          {TYPES.map((t) => {
            const on = !!prefs[t.k];
            const Icon = t.icon;
            return (
              <div key={t.k} className="ps-card p-4 flex items-center gap-3" data-testid={`pref-${t.k}`}>
                <div className="w-10 h-10 rounded-2xl bg-[#142534] flex items-center justify-center shrink-0">
                  <Icon size={18} color={t.color} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{t.l}</div>
                  <div className="text-xs text-slate-400">{t.d}</div>
                </div>
                <button
                  data-testid={`pref-toggle-${t.k}`}
                  onClick={() => toggle(t.k)}
                  disabled={saving}
                  role="switch"
                  aria-checked={on}
                  className={`relative w-12 h-7 rounded-full transition-colors ${on ? "bg-[#2fbf71]" : "bg-[#1a2f40]"}`}
                >
                  <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-slate-500">Muted types stop reaching your bell, push, and the in-app list.</p>
      </div>
    </PhoneFrame>
  );
}
